import { WizardData } from '../context/WizardContext';
import { mongoApiService } from './mongoApiService';

interface ProgressData {
  sessionId: string;
  customerId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  currentStep: number;
  stepName: string;
  formData: WizardData;
  lastUpdated: string;
  isComplete: boolean;
  createdAt: string;
  expiresAt?: string;
}

class ProgressStorageService {
  constructor() {
    // No longer need periodic processing or cleanup
    // All data is stored server-side
  }

  /**
   * Generate a unique session ID
   */
  generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Save progress to MongoDB API only
   */
  async saveProgress(
    wizardData: WizardData,
    currentStep: number,
    stepName: string,
    sessionId: string,
    isComplete: boolean = false,
    token?: string
  ): Promise<void> {
    const now = new Date().toISOString();
    const progressData: ProgressData = {
      sessionId,
      customerId: wizardData.customerId?.toString() || 'unknown',
      firstName: wizardData.personal?.client1?.firstName,
      lastName: wizardData.personal?.client1?.lastName,
      email: wizardData.personal?.contact?.email,
      phone: wizardData.personal?.contact?.phone,
      currentStep,
      stepName,
      formData: wizardData,
      lastUpdated: now,
      isComplete,
      createdAt: now,
      expiresAt: wizardData.expiresAt
    };

    // Save directly to MongoDB API only
    try {
      await mongoApiService.saveProgress(progressData, token);
      if (process.env.NODE_ENV === 'development') {
        console.log(`Progress saved to MongoDB for step ${currentStep}: ${stepName}`);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save progress to MongoDB:', error);
      }
      // Could implement a retry mechanism here if needed
      throw error;
    }
  }

  /**
   * Load progress from MongoDB by sessionId
   */
  async loadProgress(sessionId: string, token?: string): Promise<ProgressData | null> {
    try {
      const progress = await mongoApiService.getProgress(sessionId, token);
      return progress;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load progress:', error);
      }
      return null;
    }
  }

  /**
   * Get all incomplete sessions from MongoDB API
   */
  async getIncompleteSessions(token?: string): Promise<ProgressData[]> {
    try {
      const sessions = await mongoApiService.getIncompleteSessions(token);
      if (process.env.NODE_ENV === 'development') {
        console.log(`Retrieved ${sessions.length} incomplete forms from MongoDB`);
      }
      return sessions;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch incomplete sessions:', error);
      }
      return [];
    }
  }

  /**
   * Get all completed sessions from MongoDB API
   */
  async getCompletedSessions(token?: string): Promise<ProgressData[]> {
    try {
      const sessions = await mongoApiService.getCompletedSessions(token);
      if (process.env.NODE_ENV === 'development') {
        console.log(`Retrieved ${sessions.length} completed forms from MongoDB`);
      }
      return sessions;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch completed sessions:', error);
      }
      return [];
    }
  }

  /**
   * Delete a session from MongoDB API
   */
  async deleteSession(sessionId: string, token?: string): Promise<void> {
    try {
      await mongoApiService.deleteSession(sessionId, token);
      if (process.env.NODE_ENV === 'development') {
        console.log(`Deleted session ${sessionId} from MongoDB`);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to delete session:', error);
      }
      throw error;
    }
  }

  /**
   * Delete multiple sessions
   */
  async deleteSessions(sessionIds: string[], token?: string): Promise<void> {
    // Delete from MongoDB one by one
    // Could be optimized with a batch delete endpoint if needed
    for (const sessionId of sessionIds) {
      try {
        await this.deleteSession(sessionId, token);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error(`Failed to delete session ${sessionId}:`, error);
        }
      }
    }
  }
}

// Export singleton instance
export const progressStorageService = new ProgressStorageService();